// @tennis-matches/services/tournaments
// Migrated from src/services/tournaments/tournaments.remote.js
import { generateCode } from "@tennis-matches/core";
import { getSupabaseClient } from "../supabase/index.js";
import {
  isMissingRestResourceError,
  isRestEndpointServerError,
  isRestEndpointUnavailable,
  markRestEndpointUnavailable,
} from "../supabase/rest-endpoint-availability.js";

function __asStr(x) {
  return String(x ?? "").trim();
}
function __asInt(x, d = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? Math.trunc(n) : d;
}

function __normStatusFromRow(r) {
  const phase = __asStr(r?.phase).toLowerCase();
  const status = __asStr(r?.status).toLowerCase(); // if present
  const done = __asInt(r?.completed_round, 0);
  const cur = __asInt(r?.current_round, 0);
  const settings =
    r?.settings && typeof r.settings === "object" ? r.settings : {};
  const rounds = __asInt(settings?.rounds, 0);

  // finished if phase says so OR done>=rounds (if rounds known)
  if (phase === "finished" || phase === "finish") return "finished";
  if (rounds > 0 && done >= rounds) return "finished";
  return "planned";
}

// IMPORTANT: Keep JS syntax correct (no Python 'and').
function __status(r) {
  const phase = __asStr(r?.phase).toLowerCase();
  const done = __asInt(r?.completed_round, 0);
  const cur = __asInt(r?.current_round, 0);
  const settings =
    r?.settings && typeof r.settings === "object" ? r.settings : {};
  const rounds = __asInt(settings?.rounds, 0);

  if (phase === "cancelled" || phase === "canceled") return "cancelled";
  if (phase === "aborted") return "aborted";

  const isFinished =
    phase === "finished" ||
    phase === "finish" ||
    (rounds > 0 && done >= rounds);
  if (isFinished) return "finished";

  const isRunning =
    cur > 1 || done > 0 || phase === "running" || phase === "entry";
  if (isRunning) return "running";

  return "planned";
}

function __mapRpcError(error, fallback = "rpc-failed") {
  const message = String(error?.message || "");
  if (message.includes("not-authorized"))
    return { ok: false, reason: "not-authorized" };
  if (message.includes("not-authenticated"))
    return { ok: false, reason: "not-authenticated" };
  if (message.includes("tournament-not-found"))
    return { ok: false, reason: "not-found" };
  if (message.includes("tournament-archived"))
    return { ok: false, reason: "archived" };
  if (message.includes("invalid-transition"))
    return { ok: false, reason: "invalid-transition" };
  if (message.includes("not-empty")) return { ok: false, reason: "not-empty" };
  if (message.includes("missing-code"))
    return { ok: false, reason: "missing-code" };
  return { ok: false, reason: fallback, error };
}

function __stripSettingsForClone(settings) {
  const src = settings && typeof settings === "object" ? { ...settings } : {};
  delete src.lifecycle;
  delete src.results;
  delete src.ranking;
  delete src.standings;
  if (src.registration && typeof src.registration === "object") {
    const reg = { ...src.registration };
    delete reg.entrants;
    delete reg.entries;
    delete reg.participants;
    reg.open = false;
    src.registration = reg;
  }
  return src;
}

function __clearTournamentsCache() {
  _tournamentsCache = null;
  _loadTournamentsIndexInflight = null;
  _loadTournamentsIndexInflightKey = "";
}

// Cache für Turnier-Index (30 Sekunden TTL)
/** @type {{ tournaments: any[], userId: string, time: number, playerCountsLoaded: boolean, includeArchived: boolean } | null} */
let _tournamentsCache = null;
/** @type {Promise<{ok: boolean, tournaments: any[], reason?: string, error?: unknown, cached?: boolean}>|null} */
let _loadTournamentsIndexInflight = null;
/** @type {string} */
let _loadTournamentsIndexInflightKey = "";
const TOURNAMENTS_INDEX_KEY = "table:tournaments:index";
const TOURNAMENTS_CACHE_TTL_MS = 30 * 1000; // 30 Sekunden
const TOURNAMENTS_LIMIT = 100; // Maximal 100 Turniere laden
const TOURNAMENTS_QUERY_TIMEOUT_MS = 12000;

function __withTimeout(promise, ms = TOURNAMENTS_QUERY_TIMEOUT_MS) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error("tournaments-query-timeout")), ms);
    }),
  ]).catch(() => ({ data: null, error: { message: "timeout" } }));
}

async function __loadPlayersCountByCode(supabase, codes) {
  const playersCountByCode = {};
  if (!codes.length) return playersCountByCode;
  try {
    const { data: rpcRows, error: rpcError } = await supabase.rpc(
      "get_tournament_player_counts",
      {
        tournament_codes: codes,
      },
    );
    if (!rpcError && Array.isArray(rpcRows)) {
      rpcRows.forEach((row) => {
        const code = __asStr(row?.tournament_id);
        if (code) playersCountByCode[code] = __asInt(row?.player_count, 0);
      });
      return playersCountByCode;
    }

    const { data: playerCounts, error: pcError } = await supabase
      .from("tournament_players")
      .select("tournament_id")
      .in("tournament_id", codes);

    if (!pcError && Array.isArray(playerCounts)) {
      playerCounts.forEach((p) => {
        const code = __asStr(p?.tournament_id);
        if (code) {
          playersCountByCode[code] = (playersCountByCode[code] || 0) + 1;
        }
      });
    }
  } catch (e) {
    console.warn("[loadTournamentsIndex] Error loading player counts:", e);
  }
  return playersCountByCode;
}

function __mapTournamentRows(rows, playersCountByCode) {
  return rows
    .map((r) => {
      const settings =
        r?.settings && typeof r.settings === "object" ? r.settings : {};
      const code = __asStr(r?.code);
      const registrationRaw =
        settings?.registration && typeof settings.registration === "object"
          ? settings.registration
          : null;
      const registration = registrationRaw
        ? {
            open: Boolean(registrationRaw.open),
            scope: __asStr(registrationRaw.scope) || "club",
            closesAt: __asStr(registrationRaw.closesAt),
          }
        : null;
      const courtsRaw = settings?.courts;
      const courtsCount = __asInt(
        settings?.courtsCount ??
          (courtsRaw && typeof courtsRaw === "object"
            ? courtsRaw.count
            : courtsRaw),
        0,
      );
      const lifecycle =
        settings?.lifecycle && typeof settings.lifecycle === "object"
          ? settings.lifecycle
          : null;
      return {
        code,
        kind: __asStr(r?.kind) || "unknown",
        name: __asStr(r?.name),
        startDate: __asStr(settings?.startDate),
        startTime: __asStr(settings?.startTime),
        endDate: __asStr(settings?.endDate),
        endTime: __asStr(settings?.endTime),
        rounds: __asInt(settings?.rounds, 0),
        courtsCount,
        format: __asStr(settings?.format),
        motif: __asStr(settings?.motif),
        motifUrl: __asStr(settings?.motifUrl),
        motifStatus:
          settings?.motifStatus && typeof settings.motifStatus === "object"
            ? {
                text: __asStr(settings.motifStatus.text),
                bg: __asStr(settings.motifStatus.bg),
                fg: __asStr(settings.motifStatus.fg),
              }
            : null,
        phase: __asStr(r?.phase),
        currentRound: __asInt(r?.current_round, 0),
        completedRound: __asInt(r?.completed_round, 0),
        createdAt: __asStr(r?.created_at),
        updatedAt: __asStr(r?.updated_at),
        archivedAt: __asStr(r?.archived_at),
        status: __status(r),
        playersCount: __asInt(playersCountByCode[code], 0),
        registration,
        lifecycleReason: __asStr(lifecycle?.reason),
        lifecycleAction: __asStr(lifecycle?.action),
        settings,
      };
    })
    .filter((x) => x.code);
}

/**
 * Loads tournaments index from public.tournaments (your real schema).
 * Expected columns:
 * - code, kind, name, settings (jsonb), phase, current_round, completed_round, created_at, updated_at
 *
 * Performance optimizations:
 * - Caching (30s TTL)
 * - Limit (100 tournaments)
 * - Only loads when cache expired
 *
 * @param {object} [opts]
 * @param {boolean} [opts.forceRefresh]
 * @param {boolean} [opts.includePlayerCounts=true] — false: kein Zugriff auf `tournament_players` (Hub-Metadaten / Recent-Listen).
 * @param {boolean} [opts.includeArchived=false] — true: auch archivierte Turniere (archived_at gesetzt).
 */
export async function loadTournamentsIndex({
  forceRefresh = false,
  includePlayerCounts = true,
  includeArchived = false,
} = {}) {
  const cacheKey = `arch:${includeArchived ? 1 : 0}`;
  if (
    !forceRefresh &&
    _loadTournamentsIndexInflight &&
    _loadTournamentsIndexInflightKey === cacheKey
  ) {
    return _loadTournamentsIndexInflight;
  }

  const run = async () => {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, tournaments: [], reason: "no-supabase" };

    if (isRestEndpointUnavailable(TOURNAMENTS_INDEX_KEY)) {
      return { ok: false, tournaments: [], reason: "endpoint-unavailable" };
    }

    // Determine current user (scoped list: show only tournaments created by this user)
    const { data: u1, error: uErr } = await supabase.auth.getUser();
    const userId = __asStr(u1?.user?.id);
    if (uErr || !userId)
      return { ok: false, tournaments: [], reason: "not-authenticated" };

    const now = Date.now();

    // Cache-Hit (ohne Refresh): Metadaten reichen für includePlayerCounts=false immer.
    if (
      !forceRefresh &&
      _tournamentsCache &&
      _tournamentsCache.userId === userId &&
      _tournamentsCache.includeArchived === Boolean(includeArchived) &&
      now - _tournamentsCache.time < TOURNAMENTS_CACHE_TTL_MS
    ) {
      if (!includePlayerCounts) {
        return {
          ok: true,
          tournaments: _tournamentsCache.tournaments,
          cached: true,
        };
      }
      if (_tournamentsCache.playerCountsLoaded) {
        return {
          ok: true,
          tournaments: _tournamentsCache.tournaments,
          cached: true,
        };
      }
      const codes = _tournamentsCache.tournaments
        .map((t) => __asStr(t?.code))
        .filter(Boolean);
      const playersCountByCode = await __loadPlayersCountByCode(
        supabase,
        codes,
      );
      const merged = _tournamentsCache.tournaments.map((t) => ({
        ...t,
        playersCount: __asInt(playersCountByCode[__asStr(t?.code)], 0),
      }));
      _tournamentsCache = {
        tournaments: merged,
        userId,
        time: _tournamentsCache.time,
        playerCountsLoaded: true,
        includeArchived: Boolean(includeArchived),
      };
      return { ok: true, tournaments: merged, cached: true };
    }

    // NOTE: Supabase REST does not support SQL aliases; we fetch settings and map in JS.
    // Performance: Limit auf 100 Turniere + Order by created_at (sollte indexiert sein)
    // archived_at IS NULL: Soft-gelöschte Turniere bleiben in der DB, sind aber für Anwender ausgeblendet.
    let query = supabase
      .from("tournaments")
      .select(
        "code,kind,name,settings,phase,current_round,completed_round,created_at,updated_at,created_by,archived_at",
      )
      .eq("created_by", userId)
      .order("created_at", { ascending: false })
      .limit(TOURNAMENTS_LIMIT);
    if (!includeArchived) {
      query = query.is("archived_at", null);
    }

    const { data, error } = await __withTimeout(query);

    if (error) {
      if (
        isMissingRestResourceError(error) ||
        isRestEndpointServerError(error)
      ) {
        markRestEndpointUnavailable(TOURNAMENTS_INDEX_KEY);
      }
      return { ok: false, tournaments: [], reason: "db-error", error };
    }

    const rows = Array.isArray(data) ? data : [];
    const codes = rows.map((r) => __asStr(r?.code)).filter(Boolean);

    let playersCountByCode = {};
    if (includePlayerCounts && codes.length > 0) {
      playersCountByCode = await __loadPlayersCountByCode(supabase, codes);
    }

    const tournaments = __mapTournamentRows(rows, playersCountByCode);

    _tournamentsCache = {
      tournaments,
      userId,
      time: now,
      playerCountsLoaded: includePlayerCounts,
      includeArchived: Boolean(includeArchived),
    };

    return { ok: true, tournaments, cached: false };
  };

  const promise = run();
  if (!forceRefresh) {
    _loadTournamentsIndexInflight = promise;
    _loadTournamentsIndexInflightKey = cacheKey;
  }
  try {
    return await promise;
  } finally {
    if (_loadTournamentsIndexInflight === promise) {
      _loadTournamentsIndexInflight = null;
      _loadTournamentsIndexInflightKey = "";
    }
  }
}

/**
 * Deletes a tournament by code (including all related data).
 * @param {string} code - Tournament code
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function deleteTournamentByCode(code) {
  const supabase = await getSupabaseClient();
  if (!supabase || !code) {
    return { ok: false, reason: "no-supabase-or-code" };
  }

  try {
    const { data: u1, error: uErr } = await supabase.auth.getUser();
    const userId = __asStr(u1?.user?.id);
    if (uErr || !userId) return { ok: false, reason: "not-authenticated" };

    // First, get the tournament ID to delete related data
    const { data: tournament, error: fetchError } = await supabase
      .from("tournaments")
      .select("code")
      .eq("code", code)
      .eq("created_by", userId)
      .single();

    if (fetchError || !tournament) {
      return { ok: false, reason: "tournament-not-found" };
    }

    // Delete related data in correct order (FK constraints)
    // Note: tournament_id in sub-tables refers to tournaments.code (not a UUID)
    const tournamentId = code;

    // Delete match results
    const { error: resultsError } = await supabase
      .from("match_results")
      .delete()
      .eq("tournament_id", tournamentId);

    if (resultsError) {
      console.warn(
        "[deleteTournament] Error deleting match_results:",
        resultsError,
      );
    }

    // Delete tournament matches
    const { error: matchesError } = await supabase
      .from("tournament_matches")
      .delete()
      .eq("tournament_id", tournamentId);

    if (matchesError) {
      console.warn(
        "[deleteTournament] Error deleting tournament_matches:",
        matchesError,
      );
    }

    // Delete tournament rounds
    const { error: roundsError } = await supabase
      .from("tournament_rounds")
      .delete()
      .eq("tournament_id", tournamentId);

    if (roundsError) {
      console.warn(
        "[deleteTournament] Error deleting tournament_rounds:",
        roundsError,
      );
    }

    // Delete tournament players
    const { error: playersError } = await supabase
      .from("tournament_players")
      .delete()
      .eq("tournament_id", tournamentId);

    if (playersError) {
      console.warn(
        "[deleteTournament] Error deleting tournament_players:",
        playersError,
      );
    }

    // Finally, delete the tournament itself
    const { error: tournamentError } = await supabase
      .from("tournaments")
      .delete()
      .eq("code", code);

    if (tournamentError) {
      return { ok: false, reason: "delete-failed", error: tournamentError };
    }

    // Clear cache to force refresh
    __clearTournamentsCache();

    return { ok: true };
  } catch (e) {
    console.error("[deleteTournament] Unexpected error:", e);
    return { ok: false, reason: "unexpected-error", error: e };
  }
}

/**
 * Soft-Delete eines Turniers: setzt archived_at in der DB. Daten bleiben erhalten,
 * sind aber in Listen für Anwender nicht mehr sichtbar (loadTournamentsIndex filtert
 * `archived_at IS NULL`). Anmeldungen bleiben in tournament_registrations, werden
 * aber durch RPC list_open_matchmixer_registrations ebenfalls ausgeblendet.
 *
 * @param {string} code Turnier-Code
 * @returns {Promise<{ok: boolean, reason?: string, archivedAt?: string, alreadyArchived?: boolean}>}
 */
export async function archiveTournamentByCode(code) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const c = __asStr(code);
  if (!c) return { ok: false, reason: "missing-code" };

  try {
    const { data, error } = await supabase.rpc("archive_my_tournament", {
      p_code: c,
    });
    if (error) {
      console.error("[archiveTournament] RPC failed:", error);
      return __mapRpcError(error);
    }
    const payload = data && typeof data === "object" ? data : {};
    if (payload.ok !== true) {
      return { ok: false, reason: "rpc-rejected" };
    }
    __clearTournamentsCache();
    return {
      ok: true,
      archivedAt: __asStr(payload.archived_at),
      alreadyArchived: Boolean(payload.already_archived),
    };
  } catch (e) {
    console.error("[archiveTournament] Unexpected error:", e);
    return { ok: false, reason: "unexpected-error", error: e };
  }
}

export async function unarchiveTournamentByCode(code) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const c = __asStr(code);
  if (!c) return { ok: false, reason: "missing-code" };

  try {
    const { data, error } = await supabase.rpc("unarchive_my_tournament", {
      p_code: c,
    });
    if (error) {
      console.error("[unarchiveTournament] RPC failed:", error);
      return __mapRpcError(error);
    }
    const payload = data && typeof data === "object" ? data : {};
    if (payload.ok !== true) return { ok: false, reason: "rpc-rejected" };
    __clearTournamentsCache();
    return { ok: true, alreadyUnarchived: Boolean(payload.already_unarchived) };
  } catch (e) {
    console.error("[unarchiveTournament] Unexpected error:", e);
    return { ok: false, reason: "unexpected-error", error: e };
  }
}

export async function cancelTournamentByCode(code, reason = "") {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const c = __asStr(code);
  if (!c) return { ok: false, reason: "missing-code" };

  try {
    const { data, error } = await supabase.rpc("cancel_my_tournament", {
      p_code: c,
      p_reason: __asStr(reason) || null,
    });
    if (error) {
      console.error("[cancelTournament] RPC failed:", error);
      return __mapRpcError(error);
    }
    const payload = data && typeof data === "object" ? data : {};
    if (payload.ok !== true) return { ok: false, reason: "rpc-rejected" };
    __clearTournamentsCache();
    return {
      ok: true,
      phase: __asStr(payload.phase) || "cancelled",
      already: Boolean(payload.already),
    };
  } catch (e) {
    console.error("[cancelTournament] Unexpected error:", e);
    return { ok: false, reason: "unexpected-error", error: e };
  }
}

export async function abortTournamentByCode(code, reason = "") {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const c = __asStr(code);
  if (!c) return { ok: false, reason: "missing-code" };

  try {
    const { data, error } = await supabase.rpc("abort_my_tournament", {
      p_code: c,
      p_reason: __asStr(reason) || null,
    });
    if (error) {
      console.error("[abortTournament] RPC failed:", error);
      return __mapRpcError(error);
    }
    const payload = data && typeof data === "object" ? data : {};
    if (payload.ok !== true) return { ok: false, reason: "rpc-rejected" };
    __clearTournamentsCache();
    return {
      ok: true,
      phase: __asStr(payload.phase) || "aborted",
      already: Boolean(payload.already),
    };
  } catch (e) {
    console.error("[abortTournament] Unexpected error:", e);
    return { ok: false, reason: "unexpected-error", error: e };
  }
}

export async function deleteEmptyTournamentByCode(code) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const c = __asStr(code);
  if (!c) return { ok: false, reason: "missing-code" };

  try {
    const { data, error } = await supabase.rpc("delete_my_empty_tournament", {
      p_code: c,
    });
    if (error) {
      console.error("[deleteEmptyTournament] RPC failed:", error);
      return __mapRpcError(error);
    }
    const payload = data && typeof data === "object" ? data : {};
    if (payload.ok !== true) return { ok: false, reason: "rpc-rejected" };
    __clearTournamentsCache();
    return { ok: true, deleted: Boolean(payload.deleted) };
  } catch (e) {
    console.error("[deleteEmptyTournament] Unexpected error:", e);
    return { ok: false, reason: "unexpected-error", error: e };
  }
}

/**
 * Aktualisiert einzelne Settings-Felder (z. B. Termin / Motiv) ohne Subdaten zu ändern.
 * @param {string} code
 * @param {Record<string, unknown>} settingsPatch
 * @param {{ name?: string }} [opts]
 */
export async function patchTournamentSettingsByCode(
  code,
  settingsPatch = {},
  opts = {},
) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const c = __asStr(code);
  if (!c) return { ok: false, reason: "missing-code" };
  if (!settingsPatch || typeof settingsPatch !== "object") {
    return { ok: false, reason: "missing-patch" };
  }

  try {
    const { data: u1, error: uErr } = await supabase.auth.getUser();
    const userId = __asStr(u1?.user?.id);
    if (uErr || !userId) return { ok: false, reason: "not-authenticated" };

    const { data: row, error: fetchError } = await supabase
      .from("tournaments")
      .select("code,settings,name")
      .eq("code", c)
      .eq("created_by", userId)
      .maybeSingle();

    if (fetchError || !row)
      return { ok: false, reason: "not-found", error: fetchError };

    const prev =
      row?.settings && typeof row.settings === "object" ? row.settings : {};
    const nextSettings = { ...prev, ...settingsPatch };
    const updatePayload = {
      settings: nextSettings,
      updated_at: new Date().toISOString(),
    };
    const nextName = __asStr(opts?.name);
    if (nextName) updatePayload.name = nextName;

    const { error: updateError } = await supabase
      .from("tournaments")
      .update(updatePayload)
      .eq("code", c)
      .eq("created_by", userId);

    if (updateError)
      return { ok: false, reason: "update-failed", error: updateError };
    __clearTournamentsCache();
    return { ok: true, settings: nextSettings };
  } catch (e) {
    console.error("[patchTournamentSettings] Unexpected error:", e);
    return { ok: false, reason: "unexpected-error", error: e };
  }
}

/**
 * Klont Settings eines Turniers als neuen geplanten Entwurf (ohne Spieler/Matches/Results/Anmeldungen).
 * @param {{ sourceCode: string, startDate: string, startTime?: string, name?: string }} args
 */
export async function cloneTournamentAsDraft({
  sourceCode,
  startDate,
  startTime = "",
  name,
} = {}) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const src = __asStr(sourceCode);
  const sd = __asStr(startDate);
  if (!src || !sd) return { ok: false, reason: "missing-params" };

  try {
    const { data: u1, error: uErr } = await supabase.auth.getUser();
    const userId = __asStr(u1?.user?.id);
    if (uErr || !userId) return { ok: false, reason: "not-authenticated" };

    const { data: row, error: fetchError } = await supabase
      .from("tournaments")
      .select("code,kind,name,settings,tenant_id")
      .eq("code", src)
      .eq("created_by", userId)
      .maybeSingle();

    if (fetchError || !row)
      return { ok: false, reason: "not-found", error: fetchError };

    const clonedSettings = __stripSettingsForClone(row.settings);
    clonedSettings.startDate = sd;
    clonedSettings.startTime = __asStr(startTime);

    let newCode = "";
    for (let i = 0; i < 8; i += 1) {
      const candidate = generateCode(6);
      const { data: existing } = await supabase
        .from("tournaments")
        .select("code")
        .eq("code", candidate)
        .maybeSingle();
      if (!existing) {
        newCode = candidate;
        break;
      }
    }
    if (!newCode) return { ok: false, reason: "code-collision" };

    const draftName =
      __asStr(name) || (__asStr(row.name) ? `${__asStr(row.name)}` : "") || "";

    const payload = {
      code: newCode,
      kind: __asStr(row.kind) || "schleifchen",
      name: draftName,
      settings: clonedSettings,
      phase: "idle",
      current_round: 1,
      completed_round: 0,
      created_by: userId,
      updated_at: new Date().toISOString(),
    };
    const tenantId = __asStr(row.tenant_id);
    if (tenantId) payload.tenant_id = tenantId;

    const { error: insertError } = await supabase
      .from("tournaments")
      .insert(payload);
    if (insertError)
      return { ok: false, reason: "insert-failed", error: insertError };

    __clearTournamentsCache();
    return { ok: true, code: newCode };
  } catch (e) {
    console.error("[cloneTournamentAsDraft] Unexpected error:", e);
    return { ok: false, reason: "unexpected-error", error: e };
  }
}

function __pickSettings(r) {
  return r?.settings && typeof r.settings === "object" ? r.settings : {};
}

function __mapPhaseToUiPhase(phaseRaw) {
  const p = String(phaseRaw || "")
    .trim()
    .toLowerCase();
  if (!p) return "idle";
  if (p === "finished") return "finish";
  if (p === "finish") return "finish";
  if (p === "running") return "running";
  if (p === "idle") return "idle";
  return p;
}

/**
 * Loads one tournament meta row by code from public.tournaments.
 * @param {string} code - Tournament code
 * @param {boolean} [allowPublic=false] - If true, allow loading tournaments not created by current user (for public/share-by-code views)
 * Returns: { ok:true, meta }
 */
export async function loadTournamentMetaByCode(code, allowPublic = false) {
  const supabase = await getSupabaseClient();
  if (!supabase) {
    console.log("[loadTournamentMetaByCode] No supabase client");
    return { ok: false, meta: null, reason: "no-supabase" };
  }

  const c = __asStr(code);
  if (!c) {
    console.log("[loadTournamentMetaByCode] Missing code");
    return { ok: false, meta: null, reason: "missing-code" };
  }

  console.log("[loadTournamentMetaByCode] Loading tournament meta", {
    code: c,
    allowPublic,
  });

  // For public access, we don't need authentication
  // For private access (default), require authentication and filter by created_by
  let query = supabase
    .from("tournaments")
    .select(
      "code,kind,name,settings,phase,current_round,completed_round,created_at,updated_at",
    )
    .eq("code", c);

  if (!allowPublic) {
    const { data: u1, error: uErr } = await supabase.auth.getUser();
    const userId = __asStr(u1?.user?.id);
    if (uErr || !userId) {
      console.log("[loadTournamentMetaByCode] Not authenticated", {
        uErr,
        userId,
      });
      return { ok: false, meta: null, reason: "not-authenticated" };
    }
    query = query.eq("created_by", userId);
    console.log("[loadTournamentMetaByCode] Filtering by created_by", {
      userId,
    });
  } else {
    console.log(
      "[loadTournamentMetaByCode] Public access - no created_by filter",
    );
  }

  const { data, error } = await query.limit(1).maybeSingle();

  if (error) {
    console.error("[loadTournamentMetaByCode] DB error", {
      error,
      errorCode: error.code,
      errorMessage: error.message,
      errorDetails: error.details,
      code: c,
      allowPublic,
    });
    return { ok: false, meta: null, reason: "db-error", error };
  }
  if (!data) {
    console.log(
      "[loadTournamentMetaByCode] No data found in tournaments table",
      { code: c, allowPublic },
    );

    // DEBUG: Try to check if the code exists in tournaments_bootstrap instead
    if (allowPublic) {
      try {
        const { data: bootstrapData, error: bootstrapError } = await supabase
          .from("tournaments_bootstrap")
          .select("code, meta")
          .eq("code", c)
          .limit(1)
          .maybeSingle();

        if (!bootstrapError && bootstrapData) {
          console.log(
            "[loadTournamentMetaByCode] Found in tournaments_bootstrap instead",
            {
              code: c,
              hasMeta: !!bootstrapData.meta,
              metaPhase: bootstrapData.meta?.phase,
            },
          );
        }
      } catch (e) {
        // Ignore
      }
    }

    return { ok: false, meta: null, reason: "not-found" };
  }

  console.log("[loadTournamentMetaByCode] Data found", {
    code: c,
    phase: data.phase,
    phaseType: typeof data.phase,
    hasPhase: "phase" in data,
    allKeys: Object.keys(data),
    allowPublic,
  });

  try {
    const settings = __pickSettings(data);

    const rounds = __asInt(settings?.rounds, 0);
    const minutesPerRound = __asInt(settings?.minutesPerRound, 0);

    const points =
      settings?.points && typeof settings.points === "object"
        ? {
            win: __asInt(settings.points.win, 2),
            draw: __asInt(settings.points.draw, 1),
          }
        : { win: 2, draw: 1 };

    const meta = {
      code: __asStr(data?.code),
      kind: __asStr(data?.kind) || "schleifchen",
      name: __asStr(data?.name),
      startDate: __asStr(settings?.startDate),
      startTime: __asStr(settings?.startTime), // CRITICAL: Include startTime from settings
      format: __asStr(settings?.format) || "doubles",
      rounds: rounds || 0,
      minutesPerRound: minutesPerRound || 0,
      points,
      phase: __asStr(data?.phase),
      uiPhase: __mapPhaseToUiPhase(data?.phase),
      currentRound: __asInt(data?.current_round, 1) || 1,
      completedRound: __asInt(data?.completed_round, 0) || 0,
      createdAt: __asStr(data?.created_at),
      updatedAt: __asStr(data?.updated_at),
      // CRITICAL: Preserve settings for access to all settings fields
      settings: settings || {},
    };

    console.log("[loadTournamentMetaByCode] Returning success", {
      code: c,
      phase: meta.phase,
    });
    return {
      ok: true,
      meta,
    };
  } catch (err) {
    console.error("[loadTournamentMetaByCode] Error processing data", {
      error: err,
      code: c,
    });
    return { ok: false, meta: null, reason: "processing-error", error: err };
  }
}

/**
 * Loads ranking snapshot from public.tournament_rankings by tournament code (TEXT).
 * Tries common column names: snap | ranking | data | payload.
 */
export async function loadTournamentRankingSnapByCode(code) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, snap: null, reason: "no-supabase" };

  const c = __asStr(code);
  if (!c) return { ok: false, snap: null, reason: "missing-code" };

  const { data, error } = await supabase
    .from("tournament_rankings")
    .select("*")
    .eq("tournament_id", c)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) return { ok: false, snap: null, reason: "db-error", error };
  if (!data) return { ok: false, snap: null, reason: "not-found" };

  const snap = data.snap || data.ranking || data.data || data.payload || null;

  if (!snap || typeof snap !== "object")
    return { ok: false, snap: null, reason: "empty-snap" };

  return { ok: true, snap };
}
